import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, X } from 'lucide-react';
import { API } from '../utils/apiBase';

/**
 * Site-wide Founding Member promo banner. Renders a thin strip above the
 * page with a live seat counter + countdown + CTA to Pricing. Dismissible
 * per-session via localStorage. No-op when the campaign is inactive (sold
 * out or past deadline).
 */
const DISMISS_KEY = 'ascend_founding_banner_dismissed_v1';

const formatTimeLeft = (endsAt) => {
  const end = new Date(endsAt).getTime();
  const now = Date.now();
  const ms = Math.max(0, end - now);
  const days = Math.floor(ms / 86400000);
  const hours = Math.floor((ms % 86400000) / 3600000);
  const mins = Math.floor((ms % 3600000) / 60000);
  if (days > 1) return `${days} days left`;
  if (days === 1) return `1 day, ${hours}h left`;
  if (hours > 0) return `${hours}h ${mins}m left`;
  return `${mins}m left`;
};

const FoundingMemberBanner = () => {
  const [stats, setStats] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch (_) { return false; }
  });
  const [, tick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/payments/founding-member-stats`);
        if (!cancelled) setStats(res.data);
      } catch (_) { /* silent — banner is non-critical */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    // Update the countdown every minute so it feels live.
    const iv = setInterval(() => tick((x) => x + 1), 60000);
    return () => clearInterval(iv);
  }, []);

  const timeLeft = useMemo(
    () => (stats?.ends_at ? formatTimeLeft(stats.ends_at) : ''),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stats?.ends_at, /* re-render via tick */ stats],
  );

  if (dismissed || !stats?.active) return null;

  const onDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch (_) { /* storage disabled — dismiss in-memory only */ }
    setDismissed(true);
  };

  return (
    <div
      className="w-full bg-gradient-to-r from-[#002fa7] via-[#1e40af] to-[#002fa7] text-white relative z-[60]"
      data-testid="founding-banner"
    >
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm">
        <Sparkles size={16} className="text-[#fde68a]" />
        <span className="font-semibold">
          Founding Member launch: <span className="text-[#fde68a]">{stats.discount_pct}% off</span> Yearly Pro
        </span>
        <span className="hidden sm:inline text-white/70">·</span>
        <span>{timeLeft}</span>
        <span className="hidden md:inline text-white/70">·</span>
        <span
          className="hidden md:inline text-[11px] uppercase tracking-wider text-[#fde68a] font-semibold"
          data-testid="founding-banner-no-code"
        >
          No code needed — applied at checkout
        </span>
        <Link
          to="/pricing"
          className="ml-2 bg-[#fde68a] text-[#0f1115] px-3 py-1 text-xs font-bold hover:bg-[#fbbf24] transition-colors"
          data-testid="founding-banner-cta"
        >
          Claim seat →
        </Link>
      </div>
      <button
        onClick={onDismiss}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
        aria-label="Dismiss banner"
        data-testid="founding-banner-dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default FoundingMemberBanner;
